import React, { useContext, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { navigate } from '@reach/router';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import ls from 'local-storage';
import { getAppTokenName } from '@squirrellearningltd/go-shared-utils';
import { Button, FormErrors, Loader } from '@squirrellearningltd/go-teacher-components';
import { AuthContext } from '../../services/user/AuthProvider';
import { APP_NAME } from '../../services/constants';
import { FRANCHISE_INVITATION, FRANCHISE_INVITE_ACCEPT } from './services/graphql';

const AcceptFranchiseInvite = ({ franchiseId, token }) => {
    const invitationFranchiseId = decodeURIComponent(franchiseId);
    const auth = useContext(AuthContext);
    const client = useApolloClient();
    const submitting = useRef(false);
    const [formErrors, setFormErrors] = useState([]);
    const { data, loading, error } = useQuery(FRANCHISE_INVITATION, {
        variables: { franchiseId: invitationFranchiseId, token },
        fetchPolicy: 'no-cache'
    });
    const [acceptInvite, { loading: accepting }] = useMutation(FRANCHISE_INVITE_ACCEPT);
    const { register, handleSubmit, errors } = useForm({
        validationSchema: Yup.object().shape({
            name: Yup.string().trim().required('Please enter your name'),
            password: Yup.string().required('Please enter your password'),
            passwordConfirmation: Yup.string().required('Please confirm your password')
                .oneOf([Yup.ref('password')], 'Passwords must match')
        }),
        mode: 'onBlur'
    });

    const onSubmit = async values => {
        if (submitting.current) return;
        submitting.current = true;
        setFormErrors([]);
        try {
            const { data: response } = await acceptInvite({
                variables: { franchiseId: invitationFranchiseId, token, ...values, name: values.name.trim() }
            });
            const result = response && response.franchiseInviteAccept;
            if (result && result.errors && result.errors.length) {
                setFormErrors(result.errors);
                return;
            }
            if (!result || !result.token || !result.user || !result.franchise) {
                setFormErrors(['Unable to accept the invitation. Please try again.']);
                return;
            }
            ls(getAppTokenName(APP_NAME), result.token);
            auth.setCurrentUser(result.user);
            client.writeData({ data: { loggedIn: true, forcedLogout: false } });
            await navigate(`/franchise-portal/${result.franchise.id}`, { replace: true });
        } catch (requestError) {
            setFormErrors([requestError.message]);
        } finally {
            submitting.current = false;
        }
    };

    if (loading) return <Loader />;
    if (error) return <div className="login u-m-top-3"><FormErrors errors={[error.message]} /></div>;
    const invitation = data && data.franchiseInvitation;
    if (!invitation) return (
        <div className="login u-m-top-3">
            <h1>Invitation unavailable</h1>
            <p>This invitation is invalid, expired, or has already been accepted. Please ask your franchise administrator for a new invitation.</p>
        </div>
    );

    return (
        <div className="login u-m-top-3">
            <h1 className="u-m-base-3">Accept franchise invitation</h1>
            <p>Set up your account for {invitation.franchiseName}.</p>
            <form onSubmit={handleSubmit(onSubmit)}>
                {[
                    { name: 'name', 'aria-label': 'Name', type: 'text', autoComplete: 'name', defaultValue: invitation.name || '' },
                    { name: 'password', 'aria-label': 'Password', placeholder: 'Password', type: 'password', autoComplete: 'new-password' },
                    { name: 'passwordConfirmation', 'aria-label': 'Confirm password', placeholder: 'Confirm password', type: 'password', autoComplete: 'new-password' }
                ].map(field => (
                    <div className="basic-form__group" key={field.name}>
                        <input {...field} id={`franchise-invite-${field.name}`}
                            className="basic-form__text-box" ref={register} disabled={accepting} />
                        {errors[field.name] && <p className="basic-form__hint">{errors[field.name].message}</p>}
                    </div>
                ))}
                {formErrors.length > 0 && <FormErrors errors={formErrors} />}
                <Button type="submit" primary disabled={accepting}>
                    {accepting ? 'Activating...' : 'Activate account'}
                </Button>
            </form>
        </div>
    );
};

export default AcceptFranchiseInvite;
